import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import {
  X, ExternalLink, Loader2, Clock3,
  Layers3, ChevronDown, RefreshCw, Route,
} from 'lucide-react';
import { toast } from 'sonner';
import { CommanServices } from '@/services/commanServices';
import {
  collectEvidence,
  collectEvidenceActivity,
  evidencePublisher,
  evidenceTitle,
  evidenceVerification,
  safeEvidenceUrl,
} from './foresightEvidence';
import {
  primaryClassificationOf,
  writtenLocationOf,
} from './foresightClassification';
import { operationalScopeOf } from './foresightScope';
import './foresightModalLight.css';

const firstNumber = (...values) => {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== '' && Number.isFinite(Number(value))) {
      return Number(value);
    }
  }
  return null;
};

const formatDateTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatLatency = (seconds) => {
  const value = firstNumber(seconds);
  if (value === null) return '';
  if (value < 60) return `${Math.max(0, Math.round(value))}s`;
  if (value < 3600) {
    const minutes = Math.floor(value / 60);
    const remainder = Math.round(value % 60);
    return remainder ? `${minutes}m ${remainder}s` : `${minutes}m`;
  }
  const hours = Math.floor(value / 3600);
  const minutes = Math.round((value % 3600) / 60);
  return minutes ? `${hours}h ${minutes}m` : `${hours}h`;
};

const sourceLabel = (source) => String(
  typeof source === 'string'
    ? source
    : source?.display_name ||
      source?.name ||
      source?.origin_name ||
      source?.publisher ||
      source?.origin_key ||
      ''
).trim();

const detectingSourceOf = (event) => {
  if (event?.detecting_source) return event.detecting_source;
  if (event?.connection) return event.connection;
  if (event?.source) return event.source;
  const sources = Array.isArray(event?.sources) ? event.sources : [];
  return sources.find((source) =>
    source?.is_detector === true ||
    source?.is_detecting_source === true ||
    source?.is_primary_detection === true ||
    ['detector', 'detection', 'origin'].includes(String(source?.role || source?.case_role || '').toLowerCase())
  ) || sources[0] || event?.source_name || '';
};

const eventTimeOf = (event) =>
  event?.updated_at ||
  event?.occurred_at ||
  event?.timing?.published_at ||
  event?.created_at ||
  '';

const gpsOf = (event) => {
  const gps = event?.gps || event?.location?.gps || event?.location_resolution?.gps;
  const latitude = firstNumber(gps?.latitude, gps?.lat);
  const longitude = firstNumber(gps?.longitude, gps?.lng, gps?.lon);
  if (
    latitude === null ||
    longitude === null ||
    latitude < -90 ||
    latitude > 90 ||
    longitude < -180 ||
    longitude > 180
  ) return null;
  return { latitude, longitude };
};

const evidenceMatchFor = (activity, evidence) => {
  const url = String(activity?.url || '').replace(/\/$/, '').toLowerCase();
  if (url) {
    const exact = evidence.find((item) =>
      safeEvidenceUrl(item).replace(/\/$/, '').toLowerCase() === url
    );
    if (exact) return exact;
  }
  const title = String(activity?.title || '').trim().toLowerCase();
  const publisher = String(activity?.publisher || '').trim().toLowerCase();
  return evidence.find((item) =>
    evidenceTitle(item).trim().toLowerCase() === title &&
    evidencePublisher(item).trim().toLowerCase() === publisher
  ) || null;
};

// Complete evidence for a detected hazard. Publisher/source type describes
// provenance only; verification and stance are rendered as separate fields.
const EvidenceSection = ({ event }) => {
  const [corr, setCorr] = useState(event.corroboration || null);
  const [open, setOpen] = useState(true);
  const [checking, setChecking] = useState(false);

  const [checkedAt, setCheckedAt] = useState(null);

  const [verifying, setVerifying] = useState('');

  const verify = async (intelId, status) => {
    if (!event.warning_id || verifying) return;
    setVerifying(intelId);
    try {
      const res = await CommanServices.verifyEvidence(event.warning_id, intelId, { status });
      if (res?.status === false) { toast.error(res?.message || 'Could not save verdict'); return; }
      setCorr((res?.data || res)?.corroboration || corr);
      toast.success(status === 'verified' ? 'Evidence verified — counts at full weight'
                                          : 'Marked false — excluded, origin flagged');
    } finally {
      setVerifying('');
    }
  };

  const check = async () => {
    if (!event.warning_id || checking) return;
    setChecking(true);
    try {
      const res = await CommanServices.getWarningCorroboration(event.warning_id);
      if (res?.status === false) { toast.error(res?.message || 'Evidence refresh failed'); return; }
      const fresh = res?.data?.corroboration || res?.corroboration || null;
      setCorr(fresh);
      setCheckedAt(new Date());
      const origins = fresh?.independent_origin_count ?? fresh?.independent_count ?? 0;
      const raw = fresh?.raw_report_count ?? 0;
      if (origins > 0) {
        toast.success(`Evidence refreshed — ${raw} report(s) from ${origins} independent origin(s)`);
      } else {
        toast.info('Evidence refreshed — no additional relevant reports found');
      }
    } finally {
      setChecking(false);
    }
  };

  const record = { ...event, corroboration: corr };
  const evidence = collectEvidence(record);
  const activity = collectEvidenceActivity(record);
  if (!event.warning_id && !corr && activity.length === 0) return null;

  return (
    <div className="fsm-corr-card" data-testid="corroboration-section">
      <div className="fsm-evidence-head">
        <div className="fsm-evidence-heading">
          <Layers3 size={15} strokeWidth={2} />
          <span>Evidence &amp; activity</span>
          <small>
            {activity.length} linked {activity.length === 1 ? 'record' : 'records'}
            {checkedAt && (
              <span data-testid="corroboration-checked-at">
                {' '}· checked {checkedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
          </small>
        </div>
        <span className="fsm-evidence-controls">
          {event.warning_id && (
            <button onClick={check} disabled={checking} data-testid="corroboration-check"
              className="fsm-evidence-refresh" title="Refresh linked evidence">
              {checking ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} strokeWidth={2} />} Refresh
            </button>
          )}
          {activity.length > 0 && (
            <button onClick={() => setOpen((v) => !v)} data-testid="corroboration-toggle"
              aria-label={open ? 'Collapse evidence' : 'Expand evidence'}
              aria-expanded={open}
              className={`fsm-chevron ${open ? '' : 'closed'}`}>
              <ChevronDown size={16} strokeWidth={2} />
            </button>
          )}
        </span>
      </div>
      {activity.length > 0 && open && (
        <div className="fsm-activity-list" data-testid="corroboration-evidence">
          {activity.map((entry, index) => {
            const matchedEvidence = evidenceMatchFor(entry, evidence);
            const verificationId = matchedEvidence?.intel_id || matchedEvidence?.evidence_id;
            const needsVerification =
              matchedEvidence?.verification_required === true ||
              matchedEvidence?.requires_verification === true ||
              String(evidenceVerification(matchedEvidence)).toLowerCase().includes('unverified');
            return (
              <div
                key={`${entry.key || entry.url || entry.title}-${index}`}
                className="fsm-activity-row"
                data-testid="evidence-source-item"
              >
                <div className="fsm-activity-content">
                  <div className="fsm-activity-meta">
                    <b>{entry.publisher || 'Source'}</b>
                    {entry.timestamp && <time dateTime={entry.timestamp}>{formatDateTime(entry.timestamp)}</time>}
                  </div>
                  {entry.url ? (
                    <a href={entry.url} target="_blank" rel="noreferrer" dir="auto">
                      {entry.title} <ExternalLink size={11} strokeWidth={2} />
                    </a>
                  ) : (
                    <span dir="auto">{entry.title}</span>
                  )}
                </div>
                <div className="fsm-activity-actions">
                  <span className={`fsm-activity-status status-${entry.status.toLowerCase().replaceAll(' ', '-')}`}>
                    {entry.status}
                  </span>
                  {needsVerification && verificationId && (
                    <span className="fsm-verify-actions">
                      <button onClick={() => verify(verificationId, 'verified')} disabled={!!verifying}
                        data-testid={`verify-${verificationId}`} className="fsm-verify-btn ok">
                        {verifying === verificationId ? '…' : 'Verify'}
                      </button>
                      <button onClick={() => verify(verificationId, 'false')} disabled={!!verifying}
                        data-testid={`false-${verificationId}`} className="fsm-verify-btn no">False</button>
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Preview of an external event / early warning — light premium modal per
// the design spec. Used by the ForeSight watch, its warnings list, map
// points and the warning detail page.
const EventPreviewModal = ({ event, onClose, onScenario }) => {
  const gate = event?.hazard_gate || {};
  const gateStatus = String(gate.status || '').toLowerCase();
  const actionable = gate.actionable !== false &&
    !['monitoring_evidence', 'rejected_non_hazard'].includes(gateStatus);
  const eventTitle = gate.normalized_title || event.normalized_title || event.title || 'External observation';
  const classification = primaryClassificationOf(event);
  const writtenLocation = writtenLocationOf(event);
  const gps = gpsOf(event);
  const locationHref = gps
    ? `https://www.google.com/maps?q=${gps.latitude},${gps.longitude}`
    : '';
  const operationsCountry = String(
    event?.operational_scope?.country_name ||
    event?.operational_scope?.operations_country_name ||
    event?.location?.operational_scope?.country_name ||
    event?.location?.operational_scope?.operations_country_name ||
    'Operations Area'
  );
  const scope = operationalScopeOf(event, operationsCountry);
  const distance = firstNumber(scope.distance, event?.location?.distance_km, event?.distance_km);
  const areaValue = scope.code === 'internal'
    ? 'Inside'
    : scope.code === 'external' && distance !== null
      ? `${distance.toLocaleString()} km`
      : 'Distance unresolved';
  const detector = detectingSourceOf(event);
  const detectorLabel = sourceLabel(detector) || 'Connected external source';
  const eventTime = eventTimeOf(event);
  const formattedEventTime = formatDateTime(eventTime);
  const evidenceActivity = collectEvidenceActivity(event);
  const evidenceLabels = [...new Set(
    evidenceActivity
      .map((entry) => entry.publisher)
      .filter((label) => label && label.toLowerCase() !== detectorLabel.toLowerCase())
  )];
  const originCount = firstNumber(
    event?.corroboration?.independent_origin_count,
    event?.corroboration?.independent_count
  );
  const latency = formatLatency(
    event?.timing?.detection_latency_seconds ??
    event?.detection_latency_seconds ??
    event?.detection_latency?.seconds
  );
  const sourceTypes = [
    typeof detector === 'object' ? detector?.source_type || detector?.origin_type || detector?.category : '',
    ...evidenceActivity.map((entry) => entry.sourceType),
  ].map((value) => String(value || '').toLowerCase());
  const officialSource = sourceTypes.some((value) => value.includes('official'));

  return createPortal(
    <div className="fsm-backdrop" data-testid="event-preview-modal">
      <div className="fsm-modal">
        {/* Header */}
        <div className="fsm-header">
          <div className="fsm-heading">
            <div className="fsm-badges">
              <span
                className={`fsm-scope-pill scope-${scope.code}`}
                data-testid="event-preview-scope"
              >
                <b>{scope.label}</b>
                <small>{scope.detail}</small>
              </span>
              {officialSource && <span className="fsm-official-pill">Official evidence</span>}
            </div>
            <h3 className="fsm-title">{eventTitle}</h3>
            <p className="fsm-source-line">
              <span>
                Detected by <b>{detectorLabel}</b>
                {evidenceLabels.length > 0 && (
                  <> · Evidence: {evidenceLabels.slice(0, 2).join(' · ')}
                    {evidenceLabels.length > 2 && ` +${evidenceLabels.length - 2}`}
                  </>
                )}
              </span>
              {formattedEventTime && (
                <>
                  <span aria-hidden="true">•</span>
                  <time dateTime={eventTime}>{formattedEventTime}</time>
                </>
              )}
            </p>
          </div>
          <button onClick={onClose} data-testid="event-preview-close" className="fsm-close">
            <X size={17} strokeWidth={2} />
          </button>
        </div>

        {(originCount > 0 || latency) && (
          <div className="fsm-detection-metrics">
            {originCount > 0 && (
              <span>
                <Layers3 size={13} aria-hidden="true" />
                {originCount} independent {originCount === 1 ? 'origin' : 'origins'}
              </span>
            )}
            {latency && (
              <span title="Source publication to platform visibility">
                <Clock3 size={13} aria-hidden="true" />
                Detected in {latency}
              </span>
            )}
          </div>
        )}

        <div className="fsm-classification" data-testid="event-preview-classification">
          <span className={classification.incidentType ? '' : 'is-empty'}>
            <small>Incident Type</small>
            <b>{classification.incidentType || 'Not specified'}</b>
          </span>
          <span className={classification.event ? '' : 'is-empty'}>
            <small>Event</small>
            <b>{classification.event || 'Not specified'}</b>
          </span>
          <span className={classification.trigger ? '' : 'is-empty'}>
            <small>Trigger</small>
            <b>{classification.trigger || 'Not specified'}</b>
          </span>
          <span className={writtenLocation ? '' : 'is-empty'} data-testid="event-preview-location">
            <small>Location</small>
            {locationHref ? (
              <a href={locationHref} target="_blank" rel="noreferrer">
                {writtenLocation || 'Mapped location'} <ExternalLink size={11} aria-hidden="true" />
              </a>
            ) : (
              <b>{writtenLocation || 'Location unresolved'}</b>
            )}
          </span>
          <span>
            <small>{scope.code === 'external' ? 'From Operations Area' : 'Operations Area'}</small>
            <b>{areaValue}</b>
          </span>
        </div>

        {/* Brief */}
        {event.description && (
          <div className="fsm-brief" data-testid="event-preview-brief">{event.description}</div>
        )}

        {!actionable && (
          <div className="fsm-gate-note" data-testid="event-preview-hazard-gate">
            <b>{gateStatus === 'rejected_non_hazard' ? 'Validation result · not a hazard' : 'Validation result · informational evidence'}</b>
            <span>{gate.reason || 'This observation is retained as evidence and is not currently an actionable hazard.'}</span>
          </div>
        )}

        {/* Linked evidence and verification (warnings only) */}
        <EvidenceSection event={event} />

        {actionable && onScenario && (
          <div className="fsm-build" data-testid="event-preview-build-scenario">
            <div>
              <p className="fsm-eyebrow">Scenario Builder Agent</p>
              <span>Use this detection, classification, location and complete evidence as the scenario starting point.</span>
            </div>
            <button
              type="button"
              onClick={() => {
                onScenario(event);
                onClose?.();
              }}
              data-testid="event-preview-build"
            >
              <Route size={16} strokeWidth={2} />
              Build Scenario
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default EventPreviewModal;
